/**
 * Legal copy for transparency. Have qualified counsel in India review before signing contracts,
 * running ads that reference these pages, or relying on them in disputes.
 */

function blocks(...paragraphs) {
  return { paragraphs };
}

export const legalDocs = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'April 2026',
    sections: [
      {
        h: 'Who we are',
        ...blocks(
          'ChooseRightCareer (“we”, “us”, “our”) runs this website, the Career DNA style assessments, sample previews, and school partnership programs you see described on chooserightcareer.in.',
          'We are based in India. When this policy says “you”, it means visitors, students, parents or guardians acting for a student, and authorised school staff who interact with us on behalf of an institution.',
        ),
      },
      {
        h: 'What this policy covers',
        ...blocks(
          'This policy covers personal data collected through our public pages, lead and school enquiry forms, the full assessment flow, PDF or email delivery of outputs, WhatsApp or phone enquiries linked from the site, and onboarding or reporting for school programmes.',
          'If you follow a link to someone else’s site or payment gateway, their policy applies there. We do not control third party products beyond what is needed to deliver our service.',
        ),
      },
      {
        h: 'Information we collect',
        ...blocks(
          'Identity and contact: name, email address, phone number, class or grade, school name when you tell us, and similar fields you type into forms.',
          'Assessment content: answers, timestamps, session identifiers needed to save progress, and generated Career DNA style outputs.',
          'Technical data: IP address, device and browser type, approximate location from IP, pages opened, and diagnostic logs used to keep the site secure and to fix bugs.',
          'Marketing preferences: only where you clearly opt in, for example checking a box to hear about programmes or events.',
        ),
      },
      {
        h: 'How we collect it',
        ...blocks(
          'Directly from you when you submit a form, start an assessment, email us, or message us on WhatsApp.',
          'From a school partner when they share a participant list or roster under an agreement, always limited to what they are allowed to share.',
          'Automatically through cookies, local storage, or similar technologies needed for login state, analytics, or fraud prevention. You can control many cookies from your browser settings.',
        ),
      },
      {
        h: 'Why we use personal data',
        ...blocks(
          'To deliver what you asked for: generating reports, sending PDFs or links, booking counselling, or standing up a school batch.',
          'To respond to questions, send transactional notices such as payment receipts, and improve clarity of our product copy.',
          'To secure the service: detecting abuse, protecting accounts, and meeting lawful requests from authorities when valid.',
          'To understand aggregate trends, for example which pages confuse users, in a way that does not require selling identifiable data.',
        ),
      },
      {
        h: 'AI assisted outputs',
        ...blocks(
          'Some text in reports or internal tooling may be produced or drafted with the help of automated systems. Outputs are meant to support reflection and conversation, not to replace professional advice in regulated fields such as medicine or law.',
          'We log prompts and outputs only as far as needed for quality, safety, and debugging, and we do not use your assessment answers to train public models unless we introduce a separate, clearly labelled programme and consent path.',
        ),
      },
      {
        h: 'Sharing and subprocessors',
        ...blocks(
          'We do not sell personal information as a product.',
          'We share data with service providers who help us run the business, for example cloud hosting, transactional email, form delivery, analytics, or payment processing. They receive only what they need under written terms and must protect it.',
          'For school contracts we may share cohort level or student level data with the school as described in that agreement, for example so a counsellor can meet a learner who already completed the flow.',
          'We may disclose information if the law requires it, or to protect the rights, safety, or integrity of our users and staff.',
        ),
      },
      {
        h: 'International transfers',
        ...blocks(
          'Our primary operations and storage are intended to stay in India where reasonably possible. Some tools we use may process data in other countries. When that happens we rely on appropriate safeguards required by applicable Indian law, including contracts with vendors.',
        ),
      },
      {
        h: 'Retention',
        ...blocks(
          'We keep personal data only as long as needed for the purposes above, for example while your account or school licence is active, plus a short backup window.',
          'Assessment raw answers and generated reports may be kept longer where we must meet accounting, tax, or dispute resolution needs, or where a school contract sets a retention schedule. You can ask us about typical windows for your case.',
        ),
      },
      {
        h: 'Security',
        ...blocks(
          'We use reasonable technical and organisational measures such as access controls, encryption in transit where supported, and staff training. No online service can promise perfect security. If we learn of a breach that affects you in a material way, we will follow applicable notice rules.',
        ),
      },
      {
        h: 'Your rights under Indian law',
        ...blocks(
          'Depending on the situation, you may have rights to access, correct, update, or delete personal data, to withdraw consent where processing was consent based, and to lodge a complaint with the Data Protection Board of India or other competent authority once fully operational for your category of processing.',
          'Parents or guardians may exercise rights on behalf of younger learners. Schools may route requests through their designated contact where a contract applies.',
        ),
      },
      {
        h: 'How to contact us',
        ...blocks(
          'Privacy related requests: email info@chooserightcareer.in with “Privacy request” in the subject line and enough detail that we can find your record without guessing.',
          'We aim to acknowledge serious requests within a few business days. Complex requests, especially those involving schools, may take longer while we coordinate.',
        ),
      },
      {
        h: 'Changes',
        ...blocks(
          'We will update the “Last updated” date when we materially change this policy. If a change is significant, we will also try to give a clearer notice on the site or by email where we have your address.',
        ),
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    updated: 'April 2026',
    sections: [
      {
        h: 'Agreement',
        ...blocks(
          'By using ChooseRightCareer’s website, assessments, previews, or partner programmes, you agree to these Terms of Service and to our Privacy Policy. If you do not agree, please stop using the services.',
          'If you are accepting on behalf of a school, you confirm that you have authority to bind that institution.',
        ),
      },
      {
        h: 'Who may use the services',
        ...blocks(
          'Students under 18 should use paid or data heavy flows with a parent, guardian, or school contact involved as appropriate.',
          'You promise that the information you provide is accurate to the best of your knowledge and that you will not impersonate another person or institution.',
        ),
      },
      {
        h: 'What we provide',
        ...blocks(
          'We provide career discovery tools, structured questionnaires, AI assisted reporting, educational content, optional human counselling, and school rollout support as described on the site or in a separate order form.',
          'We may change features, copy, or pricing with reasonable notice where it affects you materially. Free previews may be limited, labelled, or rotated so the experience stays honest.',
        ),
      },
      {
        h: 'No professional or emergency use',
        ...blocks(
          'Output is informational. It is not medical, legal, financial, or psychological treatment. If you are in crisis, contact local emergency services or a trusted adult immediately.',
        ),
      },
      {
        h: 'Accounts, payments, and refunds',
        ...blocks(
          'Some flows are free, others may require payment through a third party gateway. Fees, taxes, and invoices are shown at checkout or in your school contract.',
          'Refunds and credits are described in our Refund Policy at /refund-policy. That page is part of these terms by reference.',
        ),
      },
      {
        h: 'Acceptable use',
        ...blocks(
          'You agree not to misuse the services, including probing for vulnerabilities without permission, scraping at a rate that harms performance, uploading malware, harassing staff or other users, or trying to extract training data or model weights from any AI feature.',
          'We may suspend or terminate access if we reasonably believe these rules are broken.',
        ),
      },
      {
        h: 'Intellectual property',
        ...blocks(
          'Branding, question banks, software, report layouts, and aggregated insights belong to ChooseRightCareer or our licensors. You receive a limited, non transferable licence to use your personal outputs for personal learning or, for schools, for the scope written in your agreement.',
          'You may not resell our assessments, redistribute bulk PDFs outside the agreed school cohort, or remove proprietary notices.',
        ),
      },
      {
        h: 'Third party services',
        ...blocks(
          'The site may link to WhatsApp, payment providers, video call tools, or school LMS platforms. Their terms and privacy policies apply when you leave our controlled experience.',
        ),
      },
      {
        h: 'Disclaimer of warranties',
        ...blocks(
          'Except where the law does not allow exclusion, the services are provided “as is” and “as available”. We do not warrant uninterrupted or error free operation.',
        ),
      },
      {
        h: 'Limitation of liability',
        ...blocks(
          'To the maximum extent permitted by Indian law, we are not liable for indirect, incidental, special, consequential, or punitive damages, or for loss of profits, goodwill, or data, arising from use of the services.',
          'Our aggregate liability for any claim relating to the services is intended to be limited to the amount you actually paid us in the twelve months before the event giving rise to the claim, or zero if you only used free features, unless a mandatory law says otherwise.',
        ),
      },
      {
        h: 'Indemnity',
        ...blocks(
          'You agree to indemnify and hold harmless ChooseRightCareer and its team from claims brought by third parties that arise from your misuse of the services, your violation of these terms, or content you submit, except to the extent a court finds we were solely at fault.',
        ),
      },
      {
        h: 'Governing law and disputes',
        ...blocks(
          'These terms are governed by the laws of India, without regard to conflict of law rules. Courts at New Delhi, India, shall have exclusive jurisdiction, subject to any non waivable rights you may have as a consumer in your home state.',
          'We prefer to resolve concerns in good faith by email before formal proceedings wherever practical.',
        ),
      },
      {
        h: 'Contact',
        ...blocks(
          'Questions about these terms: info@chooserightcareer.in with “Terms” in the subject line, or the phone number shown in the site footer.',
        ),
      },
    ],
  },
  refund: {
    title: 'Refund Policy',
    updated: 'April 2026',
    sections: [
      {
        h: 'Overview',
        ...blocks(
          'This policy explains how we handle refunds and credits for paid services from ChooseRightCareer. Free previews, school pilots covered by a separate agreement, or promotional waivers may follow different rules that are spelled out at the time.',
        ),
      },
      {
        h: 'When we usually approve a refund or credit',
        ...blocks(
          'You paid but the service was not delivered because of a clear error on our side, for example a double charge, a failed delivery of a purchased PDF after payment cleared, or a session that we cancelled without rescheduling.',
          'You contact us within fourteen days of the payment with your transaction ID and a short description so we can trace the order.',
        ),
      },
      {
        h: 'When a refund is usually not owed',
        ...blocks(
          'Change of mind after a personalised digital report has already been generated and delivered, unless mandatory law says otherwise.',
          'School licence fees for a term or batch that has already started consumption according to the signed scope.',
          'Third party fees we cannot claw back from a gateway, though we will still try to help you raise a dispute with the provider where appropriate.',
        ),
      },
      {
        h: 'How to request',
        ...blocks(
          'Email info@chooserightcareer.in or WhatsApp the number on the site with your name, payment reference, date, and what went wrong. We investigate good faith disputes and respond with the outcome.',
        ),
      },
      {
        h: 'Timing',
        ...blocks(
          'Approved refunds are normally initiated to the original payment method within 7 to 14 business days, depending on your bank or card network. We do not control posting delays after the gateway marks the refund as sent.',
        ),
      },
      {
        h: 'Chargebacks',
        ...blocks(
          'Please message us before filing a chargeback so we can fix a billing mistake quickly. Chargebacks that reverse a valid charge may lead to suspension of further purchases until the matter is cleared.',
        ),
      },
    ],
  },
};
